import {
  Box,
  Heading,
  Image,
  SimpleGrid,
  Spinner,
  Tabs,
  Text,
  VStack
} from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"

import HomeOpportunityCard from "../components/sourcing/HomeOpportunityCard"
import SectionHeading from "../components/common/SectionHeading"
import StatCard from "../components/common/StatCard"
import StateBox from "../components/common/StateBox"
import { useActivePortfolio } from "../hooks/useActivePortfolio"
import { useHomes, usePortfolioEmbodiedCarbon, usePortfolioStats } from "../hooks/usePortfolioQueries"
import { useKanbanCards, useOpportunities } from "../hooks/useSourcingQueries"
import { formatCo2Kg } from "../utils/carbonUtils"

const fmt = {
  currency: (val: number) =>
    `£${val.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`,
  roi: (val: number | null | undefined) =>
    val != null ? `${val.toFixed(1)}%` : "–",
}

const Portfolio: React.FC = () => {
  const navigate = useNavigate()
  const { activePortfolioId, activePortfolioName } = useActivePortfolio()

  const { data: portfolioStats, isLoading: statsLoading } = usePortfolioStats(activePortfolioId)
  const { data: homesData, isLoading: homesLoading } = useHomes(activePortfolioId)
  const {
    data: kanbanCards = [],
    isLoading: kanbanLoading,
    isError: kanbanError
  } = useKanbanCards(activePortfolioId)
  const { data: sourcedOpportunities } = useOpportunities(activePortfolioId, 1)

  const dashboardLoading = statsLoading || homesLoading || kanbanLoading

  const homes = homesData?.homes ?? []
  const highestRoi = homes.reduce<number | null>((max, h) => {
    if (h.upgradesRoi == null) return max
    return max == null || h.upgradesRoi > max ? h.upgradesRoi : max
  }, null)
  const myHomesRoiValues = homes.flatMap((h) => (h.upgradesRoi != null ? [h.upgradesRoi] : []))
  const avgMyHomesRoi = myHomesRoiValues.length > 0
    ? myHomesRoiValues.reduce((a, b) => a + b, 0) / myHomesRoiValues.length
    : null

  const roiPotentialCounts = portfolioStats?.roiPotentialCounts
  const highRoiCount = roiPotentialCounts?.high ?? 0
  const sourcedCount = sourcedOpportunities?.count ?? 0
  const myOpportunitiesCount = portfolioStats?.totalHomes ?? 0
  const totalOpportunitiesCount = sourcedCount + myOpportunitiesCount

  const cards = kanbanCards ?? []
  const interestedCount = cards.filter((c) => c.kanbanStage === "interested").length
  const dueDiligenceCount = cards.filter((c) => c.kanbanStage === "due_diligence").length
  const acquisitionCount = cards.filter((c) => c.kanbanStage === "acquisition").length

  const pipelineHomes = cards.map((c) => c.scrapedProperty.home)
  const totalDeployment = pipelineHomes.reduce((sum, h) => sum + (h.upgradesTotalCost ?? 0), 0)
  const totalSavings = pipelineHomes.reduce((sum, h) => sum + (h.upgradesTotalAnnualSavings ?? 0), 0)
  const roiValues = pipelineHomes.flatMap((h) => (h.upgradesRoi != null ? [h.upgradesRoi] : []))
  const avgPipelineRoi = roiValues.length > 0 ? roiValues.reduce((a, b) => a + b, 0) / roiValues.length : null

  const totalSolarMwh = portfolioStats?.totalSolarKwh != null ? portfolioStats.totalSolarKwh / 1000 : null
  const totalSolarExportedMwh = portfolioStats?.totalSolarExportedKwh != null ? portfolioStats.totalSolarExportedKwh / 1000 : null
  const totalCo2SavingsKg = portfolioStats?.totalCo2SavingsKg ?? null

  const { data: embodiedCarbon } = usePortfolioEmbodiedCarbon(homes, totalCo2SavingsKg)

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        <Heading size="xl">{activePortfolioName}</Heading>

        <Tabs.Root defaultValue="dashboard" size="lg">
          <Tabs.List mb={6}>
            <Tabs.Trigger value="dashboard">Dashboard</Tabs.Trigger>
            <Tabs.Trigger value="properties">Properties ({kanbanCards.length})</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="dashboard">
            {dashboardLoading ? (
              <Box bg="white" p={8} borderRadius="lg" textAlign="center">
                <Spinner size="lg" />
                <Text mt={4}>Loading dashboard...</Text>
              </Box>
            ) : (
              <VStack gap={8} align="stretch">
                <VStack align="stretch" gap={0}>
                  <SectionHeading>Opportunities</SectionHeading>
                  <SimpleGrid columns={3} gap={4}>
                    <StatCard
                      label="Total opportunities"
                      value={totalOpportunitiesCount}
                      subline={`${sourcedCount} sourced · ${myOpportunitiesCount} added by you`}
                    />
                    <StatCard
                      label="ROI Potential"
                      value={highRoiCount}
                      subline={`High (8%+) · ${roiPotentialCounts?.medium ?? 0} medium`}
                    />
                    <StatCard
                      label="Highest ROI"
                      value={fmt.roi(highestRoi)}
                      subline={`Avg. ROI: ${fmt.roi(avgMyHomesRoi)}`}
                    />
                  </SimpleGrid>
                </VStack>

                <VStack align="stretch" gap={0}>
                  <SectionHeading>Pipeline</SectionHeading>
                  <SimpleGrid columns={3} gap={4} mb={4}>
                    <StatCard label="Interested (to triage)" value={interestedCount} />
                    <StatCard label="Due Diligence" value={dueDiligenceCount} />
                    <StatCard label="Acquisition" value={acquisitionCount} />
                  </SimpleGrid>
                  <SimpleGrid columns={3} gap={4}>
                    <StatCard
                      label="Total retrofit deployment"
                      value={fmt.currency(totalDeployment)}
                      subline="across all pipeline homes"
                    />
                    <StatCard
                      label="Average ROI"
                      value={fmt.roi(avgPipelineRoi)}
                      subline="for all pipeline homes"
                    />
                    <StatCard
                      label="Total energy savings"
                      value={fmt.currency(totalSavings)}
                      subline="per year"
                    />
                  </SimpleGrid>
                </VStack>

                <VStack align="stretch" gap={0}>
                  <SectionHeading>Environmental Impact</SectionHeading>
                  <SimpleGrid columns={4} gap={4}>
                    <StatCard
                      label="Solar generation (all homes)"
                      value={totalSolarMwh != null ? `${totalSolarMwh.toLocaleString("en-GB", { maximumFractionDigits: 1 })} MWh` : "–"}
                      subline="Total recommended PV output per year"
                    />
                    <StatCard
                      label="Solar exported (all homes)"
                      value={totalSolarExportedMwh != null ? `${totalSolarExportedMwh.toLocaleString("en-GB", { maximumFractionDigits: 1 })} MWh` : "–"}
                      subline="Generation minus household electricity demand"
                    />
                    <StatCard
                      label="Annual operational CO₂ saved"
                      value={totalCo2SavingsKg != null ? formatCo2Kg(totalCo2SavingsKg) : "–"}
                      subline="Pre- vs post-upgrade, across analysed homes"
                    />
                    <StatCard
                      label="Total embodied carbon"
                      value={
                        embodiedCarbon?.totalEmbodiedKgCo2e != null
                          ? formatCo2Kg(embodiedCarbon.totalEmbodiedKgCo2e)
                          : "–"
                      }
                      subline={
                        embodiedCarbon?.carbonPaybackYears != null
                          ? `Offset in ~${embodiedCarbon.carbonPaybackYears.toLocaleString("en-GB", { maximumFractionDigits: 1 })} yrs by operational savings`
                          : "Offset period unavailable"
                      }
                    />
                  </SimpleGrid>
                </VStack>
              </VStack>
            )}
          </Tabs.Content>

          <Tabs.Content value="properties">
            {kanbanLoading ? (
              <Box display="flex" justifyContent="center" py={12}>
                <Spinner size="xl" />
              </Box>
            ) : kanbanError ? (
              <StateBox>
                <Text color="red.500">
                  Error loading properties. Please try again.
                </Text>
              </StateBox>
            ) : kanbanCards.length === 0 ? (
              <StateBox>
                <VStack gap={6}>
                  <Image
                    src="/images/placeholder-empty-portfolio.png"
                    alt="No properties"
                    height={240}
                  />
                  <Text fontSize="lg" color="gray.500">
                    Mark opportunities as interested to start tracking them here.
                  </Text>
                </VStack>
              </StateBox>
            ) : (
              <VStack gap={4} align="stretch">
                {kanbanCards.map((card) => (
                  <HomeOpportunityCard
                    key={card.id}
                    home={card.scrapedProperty.home}
                    kanbanStage={card.kanbanStage}
                    onClick={(id) => navigate(`/home/${id}`)}
                    hideInterested
                  />
                ))}
              </VStack>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </VStack>
    </Box>
  )
}

export default Portfolio
